/* eslint-disable max-lines */
import {
    ArrowLeftIcon, ArrowTopRightOnSquareIcon,
    HeroArrowDownTrayIcon, HeroMagnifyingGlassMinusIcon,
    HeroMagnifyingGlassPlusIcon, NinchatRotateIcon,
    NinchatShrinkIcon,
} from 'components/Icon'

import React, {
    useEffect,
} from 'react'
import {
    buttonsRight,
    fileName,
    fileSize,
    imageWrapper,
    lightBoxButtons,
    lightBoxFooter,
    lightBoxImage,
    lightBoxOverlay,
} from './styles/LightBox'

import Button from 'components/Button/Button'
import {DEFAULT_HEADER_HEIGHT} from 'components/Embed/components/EmbedHeader/EmbedHeader'
import {EmbedContext} from 'components/EmbedContext'
import {EmbedEvent} from 'embed'
import {Icon} from 'components/Icon/types'

import {config} from 'embed/listeners'
import {downloadFile} from 'utils/download'
import {fileUtil} from 'utils/file'
import {styled} from '../../stitches/config'
import useLightbox from 'hooks/lightbox'
import {useSendToParent} from 'embed/utils/event'
import {useTranslation} from 'react-i18next'

const StyledOverlay = styled('div', lightBoxOverlay)
const StyledImage = styled('img', lightBoxImage)
const StyledButtons = styled('div', lightBoxButtons)
const StyledImageWrapper = styled('div', imageWrapper)
const StyledButtonsRight = styled('div', buttonsRight)
const StyledFooter = styled('div', lightBoxFooter)
const StyledName = styled('span', fileName)
const StyledFileSize = styled('span', fileSize)

const ZOOM_STEP = 0.4

const buttonOverrides = {'$colors-buttonTextIcon': '$colors-lightBoxText'}

const LightBoxView = (): React.ReactElement | null => {
    const {
        close,
        fileAttrs,
        isOpen,
        src,
        options,
    } = useLightbox()

    const {t} = useTranslation()
    const closeButtonRef = React.useRef<HTMLDivElement>(null)
    const imageRef = React.useRef<HTMLImageElement>(null)
    const [rotate, setRotate] = React.useState(0)
    const [zoom, setZoom] = React.useState(1)
    const [initialScale, setInitialScale] = React.useState(1)
    const [isDragging, setIsDragging] = React.useState(false)
    const isDraggingRef = React.useRef(false)
    const isTouchRef = React.useRef(false)
    const [translateX, setTranslateX] = React.useState(0)
    const [translateY, setTranslateY] = React.useState(0)
    const embed = React.useContext(EmbedContext)
    const sendToParent = useSendToParent()
    const targetOrigin = config.get('targetOrigin') || ''

    const isContainerWithHeader = !embed?.floating && embed?.containerOptions?.showHeader

    const handleKeyDown = (e: KeyboardEvent) => {
        if (e.key === 'Escape') {
            close()
        }
    }

    const handleOpenInNewTab = (e?: React.MouseEvent<HTMLButtonElement>) => {
        e?.stopPropagation()
        if (src) {
            window.open(src, '_blank')
        }
    }

    const handleDownload = (e?: React.MouseEvent<HTMLButtonElement>) => {
        e?.stopPropagation()

        if (embed) {
            // if embed then send event to handle download on parent context
            sendToParent({
                event: EmbedEvent.EmbedDownload,
                fileAttrs,
                targetOrigin,
                url: src || '',
            })
        } else if (src) {
            downloadFile(src, fileAttrs?.name)
        }
    }

    const handleRotateImage = (e?: React.MouseEvent<HTMLButtonElement>) => {
        e?.stopPropagation()
        setRotate((prev) => prev + 90)
    }

    const handleZoomIn = (e?: React.MouseEvent<HTMLButtonElement | HTMLImageElement>) => {
        e?.stopPropagation()
        setZoom((prev) => prev + ZOOM_STEP)
    }

    const handleZoomOut = (e?: React.MouseEvent<HTMLButtonElement | HTMLImageElement>) => {
        e?.stopPropagation()
        setZoom((prev) => Math.max(prev - ZOOM_STEP, 1))
    }

    const handleZoomReset = (e?: React.MouseEvent<HTMLButtonElement>) => {
        e?.stopPropagation()
        setZoom(1)
        setTranslateX(0)
        setTranslateY(0)
    }

    const handleImageLoad = () => {
        if (!imageRef.current) {
            return
        }

        const img = imageRef.current
        const {
            naturalWidth,
            naturalHeight,
        } = img

        // Calculate the scale to fit the image in the viewport
        const maxWidth = window.innerWidth * 0.95
        const maxHeight = window.innerHeight - 150

        const scaleX = maxWidth / naturalWidth
        const scaleY = maxHeight / naturalHeight
        const scale = Math.min(scaleX, scaleY, 1) // Don't upscale if image is smaller than viewport

        setInitialScale(scale)
    }

    const handleEvents = (e: React.TouchEvent<HTMLImageElement> | React.MouseEvent<HTMLImageElement>) => {
        e.preventDefault()
        e.stopPropagation()

        if (e.type === 'touchstart') {
            isTouchRef.current = true
            const event = e as React.TouchEvent<HTMLImageElement>

            if (event.touches.length === 2) {
                // pinch-to-zoom
                const initialDistance = Math.hypot(
                    event.touches[1].clientX - event.touches[0].clientX,
                    event.touches[1].clientY - event.touches[0].clientY,
                )
                const initialZoom = zoom

                const handlePinchMove = (ev: TouchEvent) => {
                    if (ev.touches.length < 2) {
                        return
                    }
                    const currentDistance = Math.hypot(
                        ev.touches[1].clientX - ev.touches[0].clientX,
                        ev.touches[1].clientY - ev.touches[0].clientY,
                    )
                    setZoom(Math.max(1, initialZoom * (currentDistance / initialDistance)))
                }

                const handlePinchEnd = () => {
                    document.removeEventListener('touchmove', handlePinchMove)
                    document.removeEventListener('touchend', handlePinchEnd)
                }

                document.addEventListener('touchmove', handlePinchMove)
                document.addEventListener('touchend', handlePinchEnd)
            } else {
                // single finger drag
                const startX = event.touches[0].clientX
                const startY = event.touches[0].clientY
                const initialTranslateX = translateX
                const initialTranslateY = translateY

                const handleTouchMove = (ev: TouchEvent) => {
                    if (ev.touches.length !== 1) {
                        return
                    }
                    setTranslateX(initialTranslateX + (ev.touches[0].clientX - startX))
                    setTranslateY(initialTranslateY + (ev.touches[0].clientY - startY))
                }

                const handleTouchEnd = () => {
                    document.removeEventListener('touchmove', handleTouchMove)
                    document.removeEventListener('touchend', handleTouchEnd)
                }

                document.addEventListener('touchmove', handleTouchMove)
                document.addEventListener('touchend', handleTouchEnd)
            }
        } else if (e.type === 'mousedown') {
            if (isTouchRef.current) {
                return
            }
            const event = e as React.MouseEvent<HTMLImageElement>
            const startX = event.clientX
            const startY = event.clientY
            const initialTranslateX = translateX
            const initialTranslateY = translateY

            const handleMouseMove = (ev: MouseEvent) => {
                setIsDragging(true)
                isDraggingRef.current = true
                setTranslateX(initialTranslateX + (ev.clientX - startX))
                setTranslateY(initialTranslateY + (ev.clientY - startY))
            }

            const handleMouseUp = () => {
                if (!isDraggingRef.current) {
                    setZoom((prev) => prev + ZOOM_STEP)
                }
                setIsDragging(false)
                isDraggingRef.current = false
                document.removeEventListener('mousemove', handleMouseMove)
                document.removeEventListener('mouseup', handleMouseUp)
            }

            document.addEventListener('mousemove', handleMouseMove)
            document.addEventListener('mouseup', handleMouseUp)
        }
    }

    useEffect(() => {
        if (isOpen) {
            setTranslateX(0)
            setTranslateY(0)
            setZoom(1)
            setRotate(0)
            setInitialScale(1)
            document.addEventListener('keydown', handleKeyDown)
            closeButtonRef.current?.querySelector('button')?.focus()
            return () => document.removeEventListener('keydown', handleKeyDown)
        }
        return undefined
    }, [isOpen])

    if (!isOpen || !src) {
        return null
    }

    return (
        <StyledOverlay
            aria-label={t('Image lightbox')}
            aria-modal='true'
            css={
                {$$headerHeight: `${embed?.containerOptions?.headerOptions?.height || DEFAULT_HEADER_HEIGHT}px`}
            }
            isContainerWithHeader={isContainerWithHeader}
            onClick={(e) => {
                e.stopPropagation()
                close()
            }}
            role='dialog'
        >
            <StyledButtons ref={closeButtonRef}>
                <div>
                    <div>
                        <Button
                            aria-label={t('Close lightbox')}
                            color='text'
                            css={buttonOverrides}
                            iconLeft={ArrowLeftIcon}
                            onPress={(e) => {
                                e?.stopPropagation()
                                close()
                            }}
                            text=''
                        />
                    </div>
                    <StyledButtonsRight>
                        <Button
                            aria-label={t('Rotate')}
                            color='text'
                            css={buttonOverrides}
                            iconLeft={NinchatRotateIcon as unknown as Icon}
                            onPress={handleRotateImage}
                            text=''
                        />
                        {zoom > 1 && (
                            <Button
                                aria-label={t('Reset zoom')}
                                color='text'
                                css={buttonOverrides}
                                // eslint-disable-next-line react/no-unstable-nested-components
                                iconLeft={() => <NinchatShrinkIcon id='shrink-icon' />}
                                onPress={handleZoomReset}
                                text=''
                            />)}
                        <Button
                            aria-label={t('Zoom out')}
                            color='text'
                            css={buttonOverrides}
                            disabled={zoom <= 1}
                            iconLeft={HeroMagnifyingGlassMinusIcon as unknown as Icon}
                            onPress={handleZoomOut}
                            text=''
                        />
                        <Button
                            aria-label={t('Zoom in')}
                            color='text'
                            css={buttonOverrides}
                            iconLeft={HeroMagnifyingGlassPlusIcon as unknown as Icon}
                            onPress={handleZoomIn}
                            text=''
                        />
                        <Button
                            aria-label={t('Open in new tab')}
                            color='text'
                            css={buttonOverrides}
                            iconLeft={ArrowTopRightOnSquareIcon as unknown as Icon}
                            onPress={handleOpenInNewTab}
                            text=''
                        />
                        {!options?.downloadDisabled &&
                            <Button
                                aria-label={t('Download')}
                                color='text'
                                css={buttonOverrides}
                                iconLeft={HeroArrowDownTrayIcon as unknown as Icon}
                                onPress={handleDownload}
                                text=''
                            />}
                    </StyledButtonsRight>
                </div>
            </StyledButtons>
            <StyledImageWrapper>
                <div>
                    <StyledImage
                        alt={fileAttrs?.name}
                        css={{
                            $$rotate: `${rotate}deg`,
                            $$translateX: `${translateX}px`,
                            $$translateY: `${translateY}px`,
                            $$zoom: initialScale * zoom,
                            cursor: isDragging ? 'grabbing' : 'grab',
                        }}
                        onClick={(e) => {
                            e.stopPropagation()
                        }}
                        onLoad={handleImageLoad}
                        onMouseDown={handleEvents}
                        onTouchStart={handleEvents}
                        ref={imageRef}
                        src={src}
                    />
                </div>
            </StyledImageWrapper>
            {!!fileAttrs &&
                <StyledFooter onClick={(e) => e.stopPropagation()}>
                    <StyledName>{fileAttrs?.name}</StyledName>
                    <StyledFileSize>{fileAttrs?.size ? `${fileUtil.formatBytes(fileAttrs.size)}` : null}</StyledFileSize>
                </StyledFooter>}
        </StyledOverlay>
    )
}

export default LightBoxView
